import React, { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PwaInstall from "@/components/PwaInstall";
import InstallButton from "@/components/InstallButton";
import RenewalReminder from "@/components/RenewalReminder";
import {
  BookOpen, MessageSquare, GraduationCap, Trophy, Sparkles,
  LayoutDashboard, Shield, LogOut, Menu, X, CreditCard,
} from "lucide-react";

const AVATARS = {
  owl: "🦉", robot: "🤖", fox: "🦊", rocket: "🚀", lion: "🦁",
};

const NAV = [
  { to: "/panel", label: "Panelim", icon: LayoutDashboard },
  { to: "/konular", label: "Konu Anlatımı", icon: BookOpen },
  { to: "/soru-cevap", label: "Soru-Cevap", icon: MessageSquare },
  { to: "/denemeler", label: "Deneme Sınavı", icon: GraduationCap },
  { to: "/ilerleme", label: "İlerleme", icon: Trophy },
  { to: "/odemelerim", label: "Ödemelerim", icon: CreditCard },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
      isActive ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
    }`;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/panel" className="flex items-center gap-2" data-testid="nav-logo">
            <img src="/logo192.png" alt="NoktaDers" className="w-10 h-10 rounded-xl" />
            <span className="font-display text-xl font-bold text-indigo-700 hidden sm:block">NoktaDers</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkClass} data-testid={`nav-${n.to.slice(1)}`}>
                <n.icon className="w-4 h-4" /> {n.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink to="/admin" className={linkClass} data-testid="nav-admin">
                <Shield className="w-4 h-4" /> Yönetim
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden md:block"><InstallButton label="Uygulamayı Ekle" /></div>
            {!user?.is_subscribed && (
              <Link
                to="/abonelik"
                data-testid="nav-subscribe-btn"
                className="hidden sm:flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold px-3 py-2 rounded-xl"
              >
                <Sparkles className="w-4 h-4" /> Premium
              </Link>
            )}
            <div className="flex items-center gap-2 bg-slate-100 rounded-xl pl-1 pr-3 py-1">
              <span className="text-2xl">{AVATARS[user?.avatar] || "🦉"}</span>
              <span className="text-sm font-semibold text-slate-700 hidden sm:block max-w-[100px] truncate">
                {user?.name}
              </span>
            </div>
            <button onClick={doLogout} data-testid="logout-btn" className="p-2 text-slate-500 hover:text-rose-600 hidden lg:block">
              <LogOut className="w-5 h-5" />
            </button>
            <button className="lg:hidden p-2 text-slate-700" onClick={() => setOpen(!open)} data-testid="menu-toggle">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <nav className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1 animate-float-up">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className={linkClass}>
                <n.icon className="w-4 h-4" /> {n.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink to="/admin" onClick={() => setOpen(false)} className={linkClass}>
                <Shield className="w-4 h-4" /> Yönetim
              </NavLink>
            )}
            <NavLink to="/abonelik" onClick={() => setOpen(false)} className={linkClass}>
              <Sparkles className="w-4 h-4" /> Premium Abonelik
            </NavLink>
            <div className="px-3 py-2"><InstallButton label="Uygulamayı Cihaza Ekle" /></div>
            <button onClick={doLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-rose-600 hover:bg-rose-50">
              <LogOut className="w-4 h-4" /> Çıkış Yap
            </button>
          </nav>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
      <RenewalReminder />
      <PwaInstall />
    </div>
  );
}

export { AVATARS };
